"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import LeadFormHeader from "./leadform-header";
import SignInSection from "./signin-section";
import LeadFormFeilds from "./leadformfeilds";
import LeadformSigninModal from "./leadform-signin-modal";
import { useAuthSession } from "@packages/hooks/useAuthSession";
import {
  getCallbackUrl,
  clearCallbackUrl,
} from "@packages/lib/utlils/non-advert-pdf-utils";
import { triggerSigninToast } from "@packages/shared-components/common-utilities/toast/signin-toast";
import { setDocumentCookie } from "@packages/lib/utlils/cookie-utils";
import {
  ClientConsentItem,
  LeadFormCmsItem,
  LeadFormPageType,
  LeadFormSubmissionContext,
} from "./types";
import {
  deriveLeadFormGAContext,
  getLeadFormGAContext,
  setLeadFormGAContext,
  trackRequestInfoPageView,
  trackProspectusPageView,
  trackRequestInfoSubmit,
  trackProspectusSubmit,
  trackProspectusStep1Submit,
  trackSigninHyperlinkClick,
} from "./ga-tracking";
import { isPGS } from "@packages/lib/utlils/commonFunction";

type LeadFormSectionProps = {
  title: string;
  description: string;
  notificationMessage?: string;
  signInHeading?: string;
  signInDescription?: React.ReactNode;
  pageType?: LeadFormPageType;
  cmsItems: LeadFormCmsItem[];
  submissionContext: LeadFormSubmissionContext;
  submitLabel?: string;
  onSubmitSuccess?: (formValues?: { yearOfEntry?: string; marketingConsent?: string }) => void;
  studyLevelOptions?: { id: number; value: string; label: string }[];
  openDayOptions?: string[];
  yearOptions?: string[];
  enquiryPlaceholder?: string;
  currentStep?: number;
  totalSteps?: number;
  allCmsItems?: LeadFormCmsItem[];
  hideSignIn?: boolean;
  clientConsents?: ClientConsentItem[];
  userDetails?: any;
};

const LeadFormSection = ({
  title,
  description,
  signInHeading,
  signInDescription,
  pageType,
  cmsItems,
  submissionContext,
  submitLabel,
  onSubmitSuccess,
  studyLevelOptions,
  openDayOptions,
  yearOptions,
  enquiryPlaceholder,
  currentStep,
  totalSteps,
  allCmsItems,
  hideSignIn = false,
  clientConsents,
  userDetails,
}: LeadFormSectionProps) => {
  const router = useRouter();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const { isAuthenticated, isAuthChecked } = useAuthSession({ redirectOnUnauthenticated: false });

  // GA: Set context and fire page_view on mount
  const hasTrackedPageView = useRef(false);
  useEffect(() => {
    if (!hasTrackedPageView.current && pageType) {
      const referrer = typeof document !== "undefined" ? document.referrer : "";
      const referrerPath = referrer ? new URL(referrer).pathname : "";
      const gaCtxData = deriveLeadFormGAContext(pageType, referrerPath);
      setLeadFormGAContext({
        ...gaCtxData,
        providerType: submissionContext.subOrderItemId ? "advertiser" : "non_advertiser",
      });

      const gaCtx = getLeadFormGAContext();
      if (pageType === "requestinfo") {
        trackRequestInfoPageView(gaCtx, submissionContext.collegeId || submissionContext.institutionId, submissionContext.institutionNameDisplay, submissionContext.courseName);
      } else if (pageType === "digitalprospectus" || pageType === "physicalprospectus") {
        trackProspectusPageView(gaCtx, submissionContext.collegeId || submissionContext.institutionId, submissionContext.institutionNameDisplay, submissionContext.courseName);
      }

      // Set gaPageName in localStorage for downstream clickstream events (signin modal, authverification)
      if (typeof window !== "undefined") {
        const pathname = window.location.pathname;
        if (pathname.includes("/degrees/email") || pathname.includes("/pgs/email")) {
          localStorage.setItem("gaPageName", "enquiry_form");
        } else if (pathname.includes("/degrees/orderprospectus.html") || pathname.includes("/pgs/orderprospectus.html")) {
          localStorage.setItem("gaPageName", "bulk_prospectus_form");
        } else if (pathname.includes("/degrees/prospectus") || pathname.includes("/pgs/prospectus")) {
          localStorage.setItem("gaPageName", "prospectus_form");
        }
      }

      hasTrackedPageView.current = true;
    }
  }, [pageType]);

  const resolveSafeSameOriginPath = (callbackUrl: string | null): string | null => {
    if (!callbackUrl || typeof window === "undefined") return null;
    try {
      const parsed = new URL(callbackUrl, window.location.origin);
      if (parsed.origin !== window.location.origin) return null;
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return null;
    }
  };

  /** Enrich submissionContext with client-side request_url and referrer_url */
  const enrichedSubmissionContext = useMemo<LeadFormSubmissionContext>(() => {
    if (typeof window === "undefined") return submissionContext;
    const referrer = document.referrer || getCallbackUrl() || "";
    return {
      ...submissionContext,
      requestUrl: submissionContext.requestUrl || window.location.href,
      referrerUrl: submissionContext.referrerUrl || referrer,
    };
  }, [submissionContext]);

  /** After final submission, redirect back to the page the user came from via callbackUrl cookie */
  const handleSubmitSuccess = useCallback((formValues?: { yearOfEntry?: string; marketingConsent?: string }) => {
    // Let the parent handle its own logic first (e.g. multi-step navigation)
    onSubmitSuccess?.(formValues);

    // Only redirect on the final step (single-step forms or last step of multi-step)
    const isMultiStep = (totalSteps ?? 1) > 1;
    const isLastStep = (currentStep ?? 0) >= (totalSteps ?? 1) - 1;
    if (isMultiStep && !isLastStep) {
      // GA: fire step 1 submit event for prospectus multi-step forms
      if (pageType === "digitalprospectus" || pageType === "physicalprospectus") {
        const gaCtx = getLeadFormGAContext();
        trackProspectusStep1Submit(gaCtx, submissionContext.collegeId || submissionContext.institutionId, submissionContext.institutionNameDisplay, submissionContext.courseName, formValues?.yearOfEntry, formValues?.marketingConsent);
      }
      return;
    }

    // GA: fire revenue_kpi event on successful form submission
    const gaCtx = getLeadFormGAContext();
    if (pageType === "requestinfo") {
      trackRequestInfoSubmit(gaCtx, submissionContext.collegeId || submissionContext.institutionId, submissionContext.institutionNameDisplay, submissionContext.courseName, formValues?.yearOfEntry, formValues?.marketingConsent);
    } else if (pageType === "digitalprospectus" || pageType === "physicalprospectus") {
      trackProspectusSubmit(gaCtx, submissionContext.collegeId || submissionContext.institutionId, submissionContext.institutionNameDisplay, submissionContext.courseName, formValues?.yearOfEntry, formValues?.marketingConsent);
    }

    // Prospectus flows: redirect back to the referrer page for PGS,
    // or redirect to success path variant for CloudFront routing to Java for non-PGS.
    if (pageType === "digitalprospectus" || pageType === "physicalprospectus") {
      if (isPGS()) {
        triggerSigninToast("Your prospectus request has been sent");
        setDocumentCookie({ name: "showtoast", value: "Your prospectus request has been sent" });
        const callbackUrl = resolveSafeSameOriginPath(getCallbackUrl()) || resolveSafeSameOriginPath(document.referrer);
        clearCallbackUrl();
        window.location.href = callbackUrl || "/";
      } else if (typeof window !== "undefined") {
        setDocumentCookie({ name: "showtoast", value: "Your prospectus request has been sent" });
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set("successpageflag", "Y");
        // Change path to success variant for CloudFront routing to Java
        let successPath = nextUrl.pathname;
        if (successPath.includes("/order-prospectus") && !successPath.includes("/order-prospectus-success")) {
          successPath = successPath.replace("/order-prospectus", "/order-prospectus-success");
        } else if (successPath.includes("/orderprospectus.html")) {
          successPath = successPath.replace("/orderprospectus.html", "/orderprospectus-success.html");
        }
        window.location.href = `${successPath}${nextUrl.search}${nextUrl.hash}`;
      } else {
        router.push("/");
      }
      return;
    }

    // Request info: redirect back to the referrer page for PGS,
    // or return to source page via callback cookie for non-PGS.
    if (isPGS()) {
      triggerSigninToast("Your request has been sent");
      setDocumentCookie({ name: "showtoast", value: "Your request has been sent" });
      const callbackUrl = resolveSafeSameOriginPath(getCallbackUrl()) || resolveSafeSameOriginPath(document.referrer);
      clearCallbackUrl();
      window.location.href = callbackUrl || "/";
    } else {
      triggerSigninToast("Your request has been sent");
      setDocumentCookie({ name: "showtoast", value: "Your request has been sent" });
      const callbackUrl = resolveSafeSameOriginPath(getCallbackUrl());
      clearCallbackUrl();
      window.location.href = callbackUrl || "/";
    }
  }, [onSubmitSuccess, currentStep, totalSteps, router, pageType]);

  return (
    <div className="border-y md:border border-grey-200 md:rounded-[8px] overflow-hidden">
      <div className="bg-white p-[32px_16px] md:p-[32px] flex flex-col gap-[24px]">
        {!isAuthChecked ? (
          <div className="flex flex-col gap-[24px] items-center justify-center py-[48px]">
            <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5 12C4.5 7.875 7.875 4.5 12 4.5C16.125 4.5 19.5 7.875 19.5 12H22C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22V19.5C7.875 19.5 4.5 16.125 4.5 12Z" fill="#4A3AFF" />
            </svg>
          </div>
        ) : (
          <>
            <LeadFormHeader title={title} description={description} />
            {/* Hide social sign-in section when user is already authenticated or explicitly hidden */}
            {!isAuthenticated && !hideSignIn && (
              <SignInSection
                heading={signInHeading}
                description={signInDescription}
                onSignInClick={() => {
                  trackSigninHyperlinkClick(getLeadFormGAContext());
                  setShowRegistrationModal(true);
                }}
              />
            )}
            <LeadFormFeilds
              pageType={pageType}
              cmsItems={cmsItems}
              submissionContext={enrichedSubmissionContext}
              submitLabel={submitLabel}
              onSubmitSuccess={handleSubmitSuccess}
              studyLevelOptions={studyLevelOptions}
              openDayOptions={openDayOptions}
              yearOptions={yearOptions}
              enquiryPlaceholder={enquiryPlaceholder}
              currentStep={currentStep}
              totalSteps={totalSteps}
              allCmsItems={allCmsItems}
              isAuthenticated={isAuthenticated}
              clientConsents={clientConsents}
              userDetails={userDetails}
            />
          </>
        )}
      </div>
      {showRegistrationModal && (
        <LeadformSigninModal onClose={() => setShowRegistrationModal(false)} />
      )}
    </div>
  );
};

export default LeadFormSection;
